/**
 * Helpers de formatação de CPF/CNPJ.
 *
 * Convenção do contrato BFF↔KB: documento trafega **sem máscara** (apenas dígitos).
 * Máscara é aplicada só na exibição.
 */

/** `12345678000190` → `12.345.678/0001-90`. Já formatado passa direto. */
export function formatCnpj(raw: string): string {
  if (!raw) return '';
  if (raw.includes('.') || raw.includes('/')) return raw;
  const d = raw.replace(/\D/g, '').padStart(14, '0');
  if (d.length !== 14) return raw;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

/** `12345678901` → `123.456.789-01`. Já formatado passa direto. */
export function formatCpf(raw: string): string {
  if (!raw) return '';
  if (raw.includes('.') || raw.includes('-')) return raw;
  const d = raw.replace(/\D/g, '').padStart(11, '0');
  if (d.length !== 11) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

/** Formata por tipo de pessoa: PF→CPF, PJ→CNPJ. */
export function formatDocumento(value: string, tipoPessoa: 'PF' | 'PJ'): string {
  return tipoPessoa === 'PF' ? formatCpf(value) : formatCnpj(value);
}

/** Remove tudo que não é dígito. Útil para normalizar input antes do submit. */
export function stripDocumentoMask(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Valida dígito verificador de CPF (11 dígitos). Aceita string com ou sem máscara.
 * O server roda `checkCPF.Udp` upstream — front faz pré-validação para evitar round-trip.
 * Memória de projeto: `dtsadmin-cpf-cnpj-numeric` (CPF é Numeric no upstream).
 */
export function isValidCpfDv(value: string): boolean {
  const d = stripDocumentoMask(value);
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false; // todos os dígitos iguais
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number.parseInt(d[i]!, 10) * (10 - i);
  let dv = ((sum * 10) % 11) % 10;
  if (dv !== Number.parseInt(d[9]!, 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number.parseInt(d[i]!, 10) * (11 - i);
  dv = ((sum * 10) % 11) % 10;
  return dv === Number.parseInt(d[10]!, 10);
}

/** Valida dígito verificador de CNPJ (14 dígitos). Server também valida (`checkCNPJ.Udp`). */
export function isValidCnpjDv(value: string): boolean {
  const d = stripDocumentoMask(value);
  if (d.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(d)) return false;
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number.parseInt(d[i]!, 10) * w1[i]!;
  let r = sum % 11;
  const dv1 = r < 2 ? 0 : 11 - r;
  if (dv1 !== Number.parseInt(d[12]!, 10)) return false;
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) sum += Number.parseInt(d[i]!, 10) * w2[i]!;
  r = sum % 11;
  const dv2 = r < 2 ? 0 : 11 - r;
  return dv2 === Number.parseInt(d[13]!, 10);
}
